/**
 * Order list (beta feature `stock`, OD-P4): GET /api/order-list.
 *
 * Derived on every read and never stored: par minus on hand (the last count
 * plus deliveries since), rounded up to whole packs (OD-P2), grouped by
 * supplier with "No supplier" last. `show_all` also lists items at par and
 * items with no par, so the chef can add to an order by hand. Copy order and
 * Email order are built in the app from this answer (features/orderlist).
 */
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { registerLive } from '@/lib/live';

export type OrderList = components['schemas']['OrderListOut'];
export type OrderSupplier = components['schemas']['OrderSupplierOut'];
export type OrderRow = components['schemas']['OrderRowOut'];

export const orderListKeys = {
  all: ['order-list'] as const,
  list: (showAll: boolean) => ['order-list', { showAll }] as const,
};

export function useOrderList(showAll: boolean, enabled = true) {
  return useQuery({
    queryKey: orderListKeys.list(showAll),
    queryFn: () => unwrap(api.GET('/api/order-list', { params: { query: { show_all: showAll } } })),
    // Ticking "Show items at par" keeps the cards on screen until the longer
    // list arrives, instead of flashing the loading line.
    placeholderData: keepPreviousData,
    enabled,
  });
}

// Any ingredient change can move an order (par, supplier, price, a delete),
// and every stock change (a stocktake, a delivery) publishes
// `ingredient.stock_updated`. The `stocktake.` and `delivery.` handlers are
// belt and braces for events that carry no ingredient change.
const refreshOrders = (_evt: unknown, qc: QueryClient) => void qc.invalidateQueries({ queryKey: orderListKeys.all });
registerLive('ingredient.', refreshOrders);
registerLive('stocktake.', refreshOrders);
registerLive('delivery.', refreshOrders);
